import React, { useState } from 'react';
import { useEntorno } from '@/lib/EntornoContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lead } from '@/api/entities';
import { 
  FlaskConical, 
  Rocket, 
  AlertTriangle, 
  CheckCircle2, 
  Copy, 
  Trash2, 
  Send,
  Globe,
  Mail,
  ShieldCheck,
  X
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const WEBHOOK_TEST_URL = 'https://recibirleadwebtest-uigctblyua-ew.a.run.app';
const WEBHOOK_PROD_URL = 'https://recibirleadweb-uigctblyua-ew.a.run.app';

export default function EntornoPruebas() {
  const { entorno, isPruebas, setEntornoActivo } = useEntorno();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(isPruebas ? 'pruebas' : 'produccion');
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);

  // Form test state
  const [testNombre, setTestNombre] = useState('Test Usuario');
  const [testEmail, setTestEmail] = useState('test@ejemplo.com');
  const [testTelefono, setTestTelefono] = useState('600 000 000');
  const [testEmpresa, setTestEmpresa] = useState('Empresa Test SL');
  const [testMensaje, setTestMensaje] = useState('Quería consultar por 50 tarjetas de visita con acabado mate.');

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiado', description: 'URL copiada al portapapeles' });
  };

  const handleActivatePruebas = async () => {
    setIsSwitching(true);
    try {
      await setEntornoActivo('pruebas');
      setActiveTab('pruebas');
      toast({ title: '🧪 Modo Pruebas activado', description: 'Los leads que lleguen serán de test' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
    setIsSwitching(false);
  };

  const handleActivateProduccion = async () => {
    setIsSwitching(true);
    try {
      await setEntornoActivo('produccion');
      setConfirmSwitch(false);
      setActiveTab('produccion');
      toast({ title: '🚀 Modo Producción activado', description: 'Todo en marcha con datos reales' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
    setIsSwitching(false);
  };

  const handleSendTestLead = async () => {
    setIsSending(true);
    try {
      const response = await fetch(WEBHOOK_TEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: testNombre,
          email: testEmail,
          telefono: testTelefono,
          empresa: testEmpresa,
          comentarios: testMensaje,
        }),
      });
      const data = await response.json();
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        toast({ title: '✅ Lead de prueba enviado', description: `ID: ${data.lead_id}` });
      } else {
        throw new Error(data.error || 'Error desconocido');
      }
    } catch (e) {
      toast({ title: 'Error enviando lead test', description: e.message, variant: 'destructive' });
    }
    setIsSending(false);
  };

  const handleCleanTestLeads = async () => {
    setIsCleaning(true);
    try {
      const testLeads = await Lead.list({ entorno: 'test' });
      await Promise.all(testLeads.map(l => Lead.delete(l.id)));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ title: '🧹 Leads de prueba eliminados', description: `Se han borrado ${testLeads.length} leads` });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
    setIsCleaning(false);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <FlaskConical className="w-8 h-8 text-orange-500" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Entorno de Pruebas</h1>
          <Badge className={isPruebas
            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-300'
            : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-300'
          }>
            {isPruebas ? '🧪 PRUEBAS ACTIVO' : '🚀 PRODUCCIÓN ACTIVO'}
          </Badge>
        </div>
        <p className="text-gray-500 dark:text-gray-400">
          Gestiona los entornos de pruebas y producción. Cambia de entorno de forma segura.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('pruebas')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
            activeTab === 'pruebas'
              ? 'bg-orange-500 text-white shadow-md'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 border border-gray-200 dark:border-gray-700'
          }`}
        >
          <FlaskConical className="w-4 h-4" />
          Pruebas
        </button>
        <button
          onClick={() => setActiveTab('produccion')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
            activeTab === 'produccion'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border border-gray-200 dark:border-gray-700'
          }`}
        >
          <Rocket className="w-4 h-4" />
          Producción
        </button>
      </div>

      {/* ======================== PRUEBAS TAB ======================== */}
      {activeTab === 'pruebas' && (
        <div className="space-y-6">
          {/* Activar / Desactivar */}
          <Card className={`dark:bg-gray-900 dark:border-gray-800 ${isPruebas ? 'border-orange-300 dark:border-orange-700' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                <FlaskConical className="w-5 h-5 text-orange-500" />
                Estado del Entorno de Pruebas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isPruebas ? (
                <div className="space-y-4">
                  <Alert className="bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-700">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    <AlertDescription className="text-orange-800 dark:text-orange-300">
                      <strong>Modo Pruebas ACTIVO.</strong> Los leads que lleguen por webhook o email se marcarán como TEST y no mezclarán con los datos reales.
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={() => setConfirmSwitch(true)}
                    variant="outline"
                    className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400"
                  >
                    Desactivar Pruebas
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Activa el modo de pruebas para testear el sistema sin afectar los datos reales de producción.
                  </p>
                  <Button
                    onClick={handleActivatePruebas}
                    disabled={isSwitching}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <FlaskConical className="w-4 h-4 mr-2" />
                    {isSwitching ? 'Activando...' : 'Activar Entorno de Pruebas'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Webhook URL de pruebas */}
          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                <Globe className="w-5 h-5 text-indigo-500" />
                Webhook de Pruebas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Usa esta URL en tus formularios de prueba. Los leads creados aquí se marcan automáticamente como <Badge variant="outline" className="text-orange-600 border-orange-400">TEST</Badge>.
              </p>
              <div className="flex gap-2">
                <Input
                  value={WEBHOOK_TEST_URL}
                  readOnly
                  className="font-mono text-sm dark:bg-gray-800 dark:border-gray-700"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(WEBHOOK_TEST_URL)}
                  title="Copiar URL"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-400">Envía un POST con JSON: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{"{ nombre, email, telefono, empresa, comentarios }"}</code></p>
            </CardContent>
          </Card>

          {/* Formulario de prueba */}
          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                <Send className="w-5 h-5 text-blue-500" />
                Formulario de Prueba Rápida
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Envía un lead de prueba directamente desde aquí para verificar el flujo completo.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input value={testNombre} onChange={e => setTestNombre(e.target.value)} className="dark:bg-gray-800 dark:border-gray-700" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} className="dark:bg-gray-800 dark:border-gray-700" />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input value={testTelefono} onChange={e => setTestTelefono(e.target.value)} className="dark:bg-gray-800 dark:border-gray-700" />
                </div>
                <div>
                  <Label>Empresa</Label>
                  <Input value={testEmpresa} onChange={e => setTestEmpresa(e.target.value)} className="dark:bg-gray-800 dark:border-gray-700" />
                </div>
              </div>
              <div>
                <Label>Mensaje / Comentarios</Label>
                <Textarea
                  value={testMensaje}
                  onChange={e => setTestMensaje(e.target.value)}
                  rows={3}
                  className="dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleSendTestLead}
                  disabled={isSending}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSending ? 'Enviando...' : 'Enviar Lead de Prueba'}
                </Button>
                <Button
                  onClick={handleCleanTestLeads}
                  disabled={isCleaning}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {isCleaning ? 'Borrando...' : 'Borrar Leads TEST'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ======================== PRODUCCIÓN TAB ======================== */}
      {activeTab === 'produccion' && (
        <div className="space-y-6">
          <Card className={`dark:bg-gray-900 dark:border-gray-800 ${!isPruebas ? 'border-green-300 dark:border-green-700' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                <ShieldCheck className="w-5 h-5 text-green-500" />
                Estado del Entorno de Producción
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isPruebas ? (
                <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-800 dark:text-green-300">
                    <strong>Producción ACTIVA.</strong> El sistema está procesando leads y correos reales. Todo en marcha.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  <Alert className="bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    <AlertDescription className="text-yellow-800 dark:text-yellow-300">
                      Actualmente estás en <strong>modo Pruebas</strong>. Para activar producción y recibir leads reales, confirma abajo.
                    </AlertDescription>
                  </Alert>
                  {!confirmSwitch ? (
                    <Button
                      onClick={() => setConfirmSwitch(true)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Rocket className="w-4 h-4 mr-2" />
                      Activar Producción
                    </Button>
                  ) : (
                    <div className="p-4 border-2 border-red-300 dark:border-red-700 rounded-lg bg-red-50 dark:bg-red-900/20">
                      <p className="text-red-800 dark:text-red-300 font-semibold mb-3">
                        ⚠ ¿Estás seguro? Pasarás a recibir leads y correos reales. Cualquier error afectará a clientes reales.
                      </p>
                      <div className="flex gap-3">
                        <Button
                          onClick={handleActivateProduccion}
                          disabled={isSwitching}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          {isSwitching ? 'Activando...' : 'Sí, activar Producción'}
                        </Button>
                        <Button
                          onClick={() => setConfirmSwitch(false)}
                          variant="outline"
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info de producción */}
          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                <Globe className="w-5 h-5 text-indigo-500" />
                Webhook de Producción
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Esta es la URL real que usan tus formularios de Elementor u otras integraciones externas.
              </p>
              <div className="flex gap-2">
                <Input
                  value={WEBHOOK_PROD_URL}
                  readOnly
                  className="font-mono text-sm dark:bg-gray-800 dark:border-gray-700"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(WEBHOOK_PROD_URL)}
                  title="Copiar URL"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-gray-900 dark:border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                <Mail className="w-5 h-5 text-indigo-500" />
                Email de Producción
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                El correo de producción se configura en <strong>Configuración de Correo</strong>. 
                Consulta y edita IMAP/SMTP allí.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal confirmación salir pruebas */}
      {confirmSwitch && isPruebas && activeTab === 'pruebas' && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-opacity animate-in fade-in"
          onClick={() => setConfirmSwitch(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-xl p-6 max-w-sm w-full shadow-2xl border border-gray-200 dark:border-gray-700 mx-4 relative"
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={() => setConfirmSwitch(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 pr-6">¿Activar Producción?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Pasarás a recibir <strong>leads y correos reales</strong>. Asegúrate de que todo está configurado correctamente.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleActivateProduccion}
                disabled={isSwitching}
                className="bg-green-600 hover:bg-green-700 text-white flex-1"
              >
                {isSwitching ? 'Cambiando...' : 'Sí, pasar a Producción'}
              </Button>
              <Button variant="outline" onClick={() => setConfirmSwitch(false)} className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
